import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ABOAssignmentTable, { AssignmentData } from './ABOAssignementTable/ABOAssignmentTable';
import styles from './AccountAssignment.module.scss';
import Icon from '../common/Icon/Icon';
import { useAppSelector } from '../../redux/hooks';
import { useTranslation } from 'react-i18next';
import SeperatorLineDark from '../../assets/images/DarkTheme/seperator_line.svg';
import SeperatorLineLight from '../../assets/images/LightTheme/seperator_line.svg';
import FilterDrawerWrapper from './FilterDrawer/FilterDrawer';
import { FilterState, initialFilterState } from './FilterDrawer/FilterContext/FilterContext';
import { useLocation } from 'react-router-dom';
import {
  useLazyGetABOAssignmentListQuery,
  useLazyGetABOByAffiliateAbonumberQuery,
} from '../../redux/services/aboAssignmentAPI';
import MyAccountsLoader from '../common/Loaders/MyAccountsLoader/MyAccountsLoader';
import ErrorHandlingComponent from '../ErrorHandling/ErrorHandling';
import { PAGE_SIZE } from '../../constants/dataConstants';

import {
  useDeleteAboAssignViewMutation,
  // useGetAboAssignViewDetailsByIdQuery,
  useGetAboAssignViewsListQuery,
  useLazyGetAboAssignViewDetailsByIdQuery,
} from '../../redux/services/customiseViewAboAssignmentAPI';
import { updateViewAboAssignmentId } from '../../redux/slices/aboAssignmentViewSlice';
import { ABOAssignKeyMap } from '../../constants/dataConstants';
import { useProfileParams } from '../../utils/common';
import { useNavigate } from 'react-router-dom';
import ToastMessage from '../common/ToastMessage/ToastMessage';
import { toastMessageType } from '../../types/types';
import Modal from '../common/Modal/Modal';
import DeleteConfirmationPopUp from '../common/DeleteConfirmationPopUp/DeleteConfirmationPopUp';
import { isEmpty } from 'lodash';

type ConvertedMap<T = any> = Record<string, T[]>;
type KeyMap = Record<string, string>;

const DeleteDocConfirmationModuleContentStyle: React.CSSProperties = {
  width: '384px',
};

const customButtonStyles: React.CSSProperties = {
  textAlign: 'center',
  fontFamily: 'gt-walsheim-regular',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: '18px',
};

const AccountAssignment = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { passedAffiliate } = location.state?.data || {};
  const [isHovered, setHovered] = useState<boolean>(false);
  const currentTheme: any = useAppSelector((state: any) => state.theme.currentTheme);
  const SeperatorIcon = currentTheme === 'dark' ? SeperatorLineDark : SeperatorLineLight;

  const [selectedFilters, setSelectedFilters] = useState<FilterState>(initialFilterState);

  const [page, setPage] = useState(1);
  const [aboListData, setAboListData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isAPIError, setIsAPIError] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isAllFetched, setIsAllFetched] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [closingMonth, setClosingMonth] = useState<string>('');
  const [isForAssignment, setIsForAssignment] = useState<boolean | null>(null);
  const { affiliateCode, aboNumber, role } = useProfileParams();
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const navigate = useNavigate();

  //ABO Assignment List API
  const [aboList, { isLoading: aboListLoading, isError: aboListError }] =
    useLazyGetABOAssignmentListQuery();

  const [
    aboAssignmentList,
    { isLoading: aboForAssignmentLoading, isError: aboForAssignmentError },
  ] = useLazyGetABOByAffiliateAbonumberQuery();

  const [isLoadingAccountAssignmentList, setIsLoadingAccountAssignmentList] = useState(true);

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: 'success' as toastMessageType,
    show: false,
  });
  const [viewDetails, setViewDetails] = useState<any>(null);
  const [savedViews, setSavedViews] = useState<ViewListDataType[]>([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmationLabelText, setDeleteConfirmationLabelText] = useState('');
  const [deleteViewName, setDeleteViewName] = useState('');
  const [selectedForDeleteViewId, setSelectedForDeleteId] = useState('');
  const viewId: any = useAppSelector((state: any) => state.viewIdDetailsAboAssignment?.viewId);
  const [isViewIdChanged, setIsViewIdChanged] = useState(false);
  const defaultViewId = viewId?.defaultViewId ? viewId?.defaultViewId : viewId?.selectedViewId;
  const [isAssignClickFlag, setIsAssignClickFlag] = useState(false);

  const latestPageRef = useRef(0);
  const nextPageRef = useRef(1); // Start from page 1
  const isLoadMoreInFlightRef = useRef(false);

  // TODO : remove this after pagintion 1 call is working
  const isFromApplyOnly = defaultViewId === 0 && viewId?.selectedViewId === 0;

  const handleABOList = async (currentPage: number) => {
    const noMoreCall = totalRecords > 0 && currentPage > Math.ceil(totalRecords / PAGE_SIZE);
    if (noMoreCall) return;

    setIsLoadingMore(currentPage > 1);
    isLoadMoreInFlightRef.current = true;

    // Track this page as the latest in-flight request
    latestPageRef.current = currentPage;

    const response: any = await aboList({
      payload: {
        viewId: defaultViewId,
        viewSetting: defaultViewId === null ? null : viewDetails,
      },
      pageNumber: currentPage,
    });

    // Ignore stale responses (response for older page arrives later)
    if (latestPageRef.current !== currentPage) return;

    if (response?.data?.body?.abos) {
      // TODO : remove (currentPage === 2 && isFromApplyOnly) after pagination 1 call working
      const shouldClearList = currentPage === 1 || (currentPage === 2 && isFromApplyOnly);

      setAboListData((prev) => [...(shouldClearList ? [] : prev), ...response.data.body.abos]);

      const newTotal = response.data.body.pagination?.totalRecord || 0;
      setTotalRecords(newTotal);
      const closingMonthsFormatted = response?.data?.body?.closingMonth
        ? `${response?.data?.body?.closingMonth.slice(-2)}/${response?.data?.body?.closingMonth.slice(0, 4)}`
        : '-';
      setClosingMonth(closingMonthsFormatted);
      const fetchedAll = currentPage * PAGE_SIZE >= newTotal;
      setIsAllFetched(fetchedAll);
    } else {
      if (currentPage === 1) setAboListData([]);
    }
    setIsLoadingData(false);
    setIsLoadingMore(false);
    isLoadMoreInFlightRef.current = false;
  };

  useEffect(() => {
    if (affiliateCode && aboNumber && role && location.pathname !== '/assignment') {
      setIsForAssignment(true);
    } else {
      setIsForAssignment(false);
    }
    clearPaginationData();
  }, [affiliateCode, aboNumber, role, location.pathname]);

  const handleAssignABOList = async () => {
    const response: any = await aboAssignmentList({
      affiliateCode,
      aboNumber,
      role,
      pageNumber: page,
    });

    if (response?.data?.body?.abos) {
      setAboListData(response?.data?.body?.abos ?? []);
      const closingMonthsFormatted = response?.data?.body?.closingMonth
        ? `${response?.data?.body?.closingMonth.slice(-2)}/${response?.data?.body?.closingMonth.slice(0, 4)}`
        : '-';
      setClosingMonth(closingMonthsFormatted);
      setTotalRecords(response?.data?.body?.pagination?.totalRecord);
      if (aboListData.length === response?.data?.body?.pagination?.totalRecord) {
        setIsAllFetched(true);
      }
    } else {
      setAboListData([]);
    }
    setIsLoadingData(false);
  };

  const handleAPIError = () => {
    setIsAPIError(true);
    setClosingMonth('');
    setAboListData([]);
  };
  useEffect(() => {
    aboListError && handleAPIError();
  }, [aboListError]);

  // Get the list of all view
  const {
    isLoading: aboAssignmentViewListLoading,
    data: aboAssignmentViewListData,
    isError: aboAssignmentViewListError,
    isFetching: aboAssignmentViewListFetching,
  } = useGetAboAssignViewsListQuery(
    { type: 'abo-assignment' }
    // {skip : isForAssignment || false}
  );

  const convertMappedObjectToSelectFilterForm = <T = any,>(
    obj: ConvertedMap<T>,
    keyMap: KeyMap
  ): { key: string; values: T[] }[] => {
    // Build reverse mapping: B1 → A1
    const reverseMap: Record<string, string> = Object?.entries(keyMap).reduce(
      (acc, [originalKey, mappedKey]) => {
        acc[mappedKey] = originalKey;
        return acc;
      },
      {} as Record<string, string>
    );

    return Object?.entries(obj)
      ?.filter(([mappedKey]) => reverseMap[mappedKey])
      ?.map(([mappedKey, values]) => ({
        key: reverseMap[mappedKey],
        values,
      }));
  };

  useEffect(() => {
    setSavedViews(aboAssignmentViewListData?.body || []);
  }, [aboAssignmentViewListData]);

  // View details API

  const [
    getAboAssignmentViewDetailsData,
    {
      isError: aboAssignmentViewDetailsError,
      isLoading: aboAssignmentViewDetailsLoading,
      isFetching: aboAssignmentViewDetailsFetching,
    },
  ] = useLazyGetAboAssignViewDetailsByIdQuery();

  useEffect(() => {
    if (
      (savedViews && savedViews.length >= 1) ||
      !aboAssignmentViewListData ||
      (defaultViewId === null && viewId?.selectedViewId === null)
    ) {
      const view = savedViews && savedViews?.filter((view) => view?._default); // choose view with default true
      const defaultViews = view.sort(
        (a, b) =>
          new Date(b?.audit?.updatedDate)?.getTime() - new Date(a?.audit?.updatedDate)?.getTime()
      );

      // Get the most recent default view
      const recentDefaultView = defaultViews[0];
      if (
        (view && viewId.defaultViewId) ||
        !aboAssignmentViewListData ||
        (defaultViewId === null && viewId?.selectedViewId === null)
      ) {
        dispatch(
          updateViewAboAssignmentId({
            ...viewId,
            viewIdDetailsAboAssignment: {
              defaultViewId: recentDefaultView?.viewId || null,
              selectedViewId: null,
            },
          })
        );
      }
    }
  }, [savedViews]);

  useEffect(() => {
    (aboAssignmentViewDetailsError || aboAssignmentViewListError) &&
      setIsLoadingAccountAssignmentList(false);
  }, [aboAssignmentViewDetailsError, aboAssignmentViewListError]);

  const clearPaginationData = () => {
    setPage(1);
    setAboListData([]);
    setTotalRecords(0);
    setIsAllFetched(false);
    setIsLoadingAccountAssignmentList(true);
    setIsLoadingData(true);
    setIsLoadingMore(false);
    isLoadMoreInFlightRef.current = false;
    // Reset refs to ensure fresh start
    latestPageRef.current = 0;
    nextPageRef.current = 1;
  };

  // Pagination effect for subsequent pages
  useEffect(() => {
    if (isForAssignment) return;
    if (page > 1 && !isAllFetched) {
      handleABOList(page);
    }
  }, [page]);

  useEffect(() => {
    if (isForAssignment === null || isForAssignment) return;
    if (viewId !== undefined) {
      setIsViewIdChanged(true); // mark view switch
      clearPaginationData();
      setTotalRecords(0);
      setIsAllFetched(false);
      if (defaultViewId == null) {
        setSelectedFilters(initialFilterState);
        setViewDetails(null);
        // Fetch first page after resetting
        queueMicrotask(() => {
          handleABOList(1);
        });
        setIsViewIdChanged(false);
      } else if (defaultViewId === 0) {
        setViewDetails(viewDetails || null);
        const selecterFilterBasedViewSetting = convertMappedObjectToSelectFilterForm(
          viewDetails || [],
          ABOAssignKeyMap
        );
        setSelectedFilters(selecterFilterBasedViewSetting as FilterState);
        handleABOList(1);
        setIsViewIdChanged(false);
      } else {
        getAboAssignmentViewDetailsData({
          viewId: defaultViewId,
        })
          .unwrap()
          .then((viewDeatilsResponse: any) => {
            if (isEmpty(viewDeatilsResponse?.body)) {
              handleAPIError();
              throw new Error('Something went wrong after fetching data!');
            }
            setViewDetails(viewDeatilsResponse.body.viewSetting);
            // set filter state as per view details
            const selecterFilterBasedViewSetting = convertMappedObjectToSelectFilterForm(
              viewDeatilsResponse?.body?.viewSetting || [],
              ABOAssignKeyMap
            );
            setSelectedFilters(selecterFilterBasedViewSetting as FilterState);
            handleABOList(1);
            setIsViewIdChanged(false);
          })
          .catch((error) => {
            console.error('Error fetching viewDetails:', error);
            setIsViewIdChanged(false);
          });
      }
    }
  }, [defaultViewId, isForAssignment, isAssignClickFlag]);

  useEffect(() => {
    if (viewDetails && !isViewIdChanged) {
      // Prevent double-trigger on view switch
      clearPaginationData();
      const selecterFilterBasedViewSetting = convertMappedObjectToSelectFilterForm(
        viewDetails || [],
        ABOAssignKeyMap
      );
      setSelectedFilters(selecterFilterBasedViewSetting as FilterState);

      // Fetch fresh data for same viewId update
      queueMicrotask(() => handleABOList(1));
    }
  }, [viewDetails]);

  useEffect(() => {
    if (isForAssignment && Object.values({ affiliateCode, aboNumber, role }).every(Boolean)) {
      clearPaginationData();
      handleAssignABOList();
    }
  }, [affiliateCode, aboNumber, role, isForAssignment]);

  const handleLoadMoreAccounts = () => {
    if (isLoadingMore || isAllFetched || isForAssignment || isLoadMoreInFlightRef.current) {
      return;
    }

    isLoadMoreInFlightRef.current = true;
    setPage((prevPage) => prevPage + 1);
  };

  const handleViewSelection = (selectedView: any) => {
    navigate('/assignment', { replace: true });
    if (defaultViewId === selectedView) return;
    setIsLoadingData(true);
    setIsForAssignment(false);
    dispatch(
      updateViewAboAssignmentId({
        ...viewId,
        viewIdDetailsAboAssignment: {
          defaultViewId: selectedView,
          selectedViewId: null,
        },
      })
    );
  };

  const setToastMessage = (message: string, msgType: toastMessageType) => {
    setToastConfig({
      type: msgType,
      message: message,
      show: true,
    });
  };

  const onDeleteView = (viewId: string, viewName: string) => {
    const labelText = t('delete_view_confirmation_msg', { name: viewName });
    setDeleteConfirmationLabelText(labelText);
    setDeleteViewName(viewName);
    setSelectedForDeleteId(viewId);
    handleDeleteModalToggle();
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      setToastMessage(location.state.successMessage, 'success')
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleDeleteModalToggle = () => {
    setDeleteModalOpen(!deleteModalOpen);
  };

  const [deleteView, { isLoading: isLoadingDeleteView }] = useDeleteAboAssignViewMutation();

  const confirmDeleteView = () => {
    setDeleteModalOpen(false);
    if (selectedForDeleteViewId) {
      deleteView({
        viewId: selectedForDeleteViewId,
      }).then((response: any) => {
        if (response?.data?.statusCode === 200) {
          setToastMessage('Custom view deleted successfully', 'success');
          dispatch(
            updateViewAboAssignmentId({
              viewIdDetailsAboAssignment: {
                defaultViewId: null,
                selectedViewId: null,
              },
            })
          );
          setViewDetails(null);
          setSelectedFilters(initialFilterState);
          clearPaginationData();
        } else if (response?.data?.statusCode === 400) {
          setToastMessage(
            response?.error?.message || 'Unable to delete the view. Please try again.',
            'error'
          );
          console.error('Delete view failed');
        }
      });
    }
  };

  const isLoading =
    isLoadingData ||
    aboAssignmentViewListLoading ||
    aboAssignmentViewDetailsLoading ||
    aboForAssignmentLoading ||
    aboAssignmentViewDetailsFetching ||
    aboAssignmentViewListFetching ||
    isLoadingDeleteView;

  return (
    <div className={styles.account_assignment_container}>
      <div className={`section_title_head ${styles.section_title_head}`}>
        {t('AP_info.account_assignment')}
      </div>
      <div className={`section_sub_heading ${styles.sub_heading}`}>
        {closingMonth ? `${t('Last_closed_month')} ${closingMonth}` : ''}
      </div>

      {isLoading ? (
        <MyAccountsLoader num={6} />
      ) : isAPIError ? (
        <ErrorHandlingComponent error={'genericError'} />
      ) : (
        <>
          <div className={`btn_wrapper ${styles.btn_wrapper}`}>
            <FilterDrawerWrapper
              filterState={selectedFilters}
              setFilterState={setSelectedFilters}
              setViewDetails={setViewDetails}
              clearPaginationData={clearPaginationData}
              savedViews={savedViews}
              setToastMessage={setToastMessage}
              closingMonth={closingMonth}
            />
            <img src={SeperatorIcon} alt="seperator" />
            <div
              className={`${defaultViewId === null ? 'selected_view_btn' : ''} view_btn ${styles.view_btn}`}
              onClick={() => (defaultViewId === null ? () => {} : handleViewSelection(null))}
            >
              <span
                className={`${styles.label} ${defaultViewId === null ? styles.bold_label : ''}`}
                data-testid="default_view_testId"
              >
                {t('default_view')}
              </span>
            </div>
            {savedViews?.length > 0 &&
              savedViews.map((view: any) => (
                <div
                  className={`${defaultViewId === view?.viewId ? 'selected_view_btn' : ''} view_btn ${styles.view_btn}`}
                  onClick={() =>
                    defaultViewId === view?.viewId ? () => {} : handleViewSelection(view?.viewId)
                  }
                  key={view?.viewId}
                >
                  <span
                    className={`${styles.label} ${
                      defaultViewId === view?.viewId ? styles.bold_label : ''
                    }`}
                  >
                    {view?.name}
                  </span>
                  {defaultViewId === view?.viewId ? (
                    <Icon
                      name={'cross_circle'}
                      size={16}
                      onClick={() => onDeleteView(view?.viewId, view?.name)}
                      className={styles.delete_filter_icon}
                      color={currentTheme === 'light' ? '#ffffff' : '#000000'}
                    />
                  ) : (
                    ''
                  )}
                </div>
              ))}
          </div>
          <ABOAssignmentTable
            data={aboListData || []}
            filterOrder={selectedFilters.map((fil) => fil.key)}
            isAllRecordsFetched={isAllFetched}
            isForAssignment={isForAssignment}
            onAssignCallback={() => setIsAssignClickFlag(!isAssignClickFlag)}
            onLoadMore={handleLoadMoreAccounts}
            isLoadingMore={isLoadingMore}
          />
          <ToastMessage
            message={toastConfig.message}
            toastType={toastConfig.type}
            showToast={toastConfig.show}
            setShowToast={(show) => setToastConfig((prev) => ({ ...prev, show }))}
            customStyles={{
              top: '96px',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
            autoDismissal
          />

          <Modal
            showDrawer={deleteModalOpen}
            onClose={handleDeleteModalToggle}
            customStyle={DeleteDocConfirmationModuleContentStyle}
          >
            <DeleteConfirmationPopUp
              id={'aboAssignmentViewDeleteConfirmation'}
              titleLabel={t('delete_view_confirmation_headerText')}
              msgText={deleteConfirmationLabelText}
              buttonLabelOne={t('no_cancel')}
              buttonLabelTwo={t('yes_delete')}
              onCancel={handleDeleteModalToggle}
              onDelete={confirmDeleteView}
              customStyle={customButtonStyles}
              viewName={deleteViewName}
              isaboAssignement={true}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default AccountAssignment;
